import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

// Feature flag for skipping authentication (development/testing)
const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Don't redirect on 401 when skip-auth is enabled
    if (error.response?.status === 401 && !SKIP_AUTH) {
      // Clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API Error type
export interface ApiError {
  message: string;
  code: string;
  details?: Record<string, unknown>;
}

// Type-safe API response handler
export async function handleApiError(error: unknown): Promise<never> {
  if (axios.isAxiosError(error)) {
    const apiError: ApiError = error.response?.data || {
      message: error.message,
      code: 'NETWORK_ERROR',
    };
    throw apiError;
  }
  throw { message: 'An unexpected error occurred', code: 'UNKNOWN_ERROR' };
}

// Auth API
export const authApi = {
  async login(username: string, password: string) {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
};

// Practice API
export interface Question {
  id: string;
  type: 'quantitative' | 'verbal' | 'data_insights';
  difficulty: 'easy' | 'medium' | 'hard';
  questionContent: string;
  mathNotation?: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  optionE: string;
  correctAnswer: 'A' | 'B' | 'C' | 'D' | 'E';
  explanation: string;
  estimatedTimeSeconds: number;
  pointsValue: number;
}

export interface ScoringBreakdown {
  base: number;
  radahnApplied: boolean;
  contextModifier: number;
  postureModifier: number;
  postureWindow: 'deflect' | 'normal' | 'broken';
  frenzyMultiplier: number;
  frenzyActive: boolean;
  formula: string;
}

export interface FrenzyUpdate {
  sessionId: string;
  previousMultiplier: number;
  currentMultiplier: number;
  sessionEarnings: number;
  frenzyPenalty?: number;
  status: 'active' | 'cashed_out' | 'failed';
}

export interface RuneUpdate {
  radahnStreakCount: number;
  radahnJustUnlocked: boolean;
  rykard3xCount: number;
  rykardJustUnlocked: boolean;
}

export interface BloodstainInfo {
  vetEntryId: string;
  bloodstainValue: number;
  degraded: boolean;
}

export interface PracticeSessionResponse {
  isCorrect: boolean;
  pointsEarned: number;
  correctAnswer: 'A' | 'B' | 'C' | 'D' | 'E';
  explanation: string;
  newBalance: number;
  // Enhanced scoring fields (added by gamified scoring system)
  scoringBreakdown?: ScoringBreakdown;
  frenzyUpdate?: FrenzyUpdate | null;
  runeUpdate?: RuneUpdate;
  bloodstain?: BloodstainInfo | null;
}

export const practiceApi = {
  async getQuestion(type: string, difficulty: string): Promise<Question> {
    const response = await api.get('/practice/questions', {
      params: { type, difficulty },
    });
    return response.data;
  },

  async submitAnswer(
    questionId: string,
    selectedAnswer: string | null,
    timeTakenSeconds: number,
    wasTimedOut = false
  ): Promise<PracticeSessionResponse> {
    const response = await api.post('/practice/sessions', {
      questionId,
      selectedAnswer,
      timeTakenSeconds,
      wasTimedOut,
    });
    return response.data;
  },
};

// Progress API
export interface ProgressGardenEntry {
  id: string;
  studyDate: string;
  plantType: string;
  plantEmoji: string;
  streakDay: number;
  questionsCompleted: number;
  pointsEarned: number;
}

export interface Dashboard {
  currentStreak: number;
  longestStreak: number;
  totalPawPoints: number;
  totalPointsEarned: number;
  lastStudyDate: string | null;
  gardenEntries: ProgressGardenEntry[];
  studiedToday: boolean;
}

export const progressApi = {
  async getDashboard(): Promise<Dashboard> {
    const response = await api.get('/progress/dashboard');
    return response.data;
  },

  async getGarden(limit = 30, offset = 0): Promise<{ entries: ProgressGardenEntry[]; total: number }> {
    const response = await api.get('/progress/garden', {
      params: { limit, offset },
    });
    return response.data;
  },
};

// Vet API
export interface VetQuestion {
  vetEntryId: string;
  questionId: string;
  question: Question;
  firstIncorrectAt: string;
  incorrectCount: number;
  status?: 'unhealed' | 'healed' | 'lost' | 'mastered';
  healedAt?: string | null;
  bloodstainValue?: number;
}

export interface HealAttemptResult {
  healed: boolean;
  message: string;
  explanation?: string;
  incorrectCount?: number;
  pointsRecovered?: number;
  pointsLost?: number;
  newBalance?: number;
  status?: string;
}

export interface MasterAttemptResult {
  mastered: boolean;
  message: string;
  explanation?: string;
}

export interface ReturnToPracticeResult {
  returned: boolean;
  message: string;
}

export const vetApi = {
  async getQuestions(status = 'unhealed'): Promise<{ questions: VetQuestion[] }> {
    const response = await api.get('/vet/questions', {
      params: { status },
    });
    return response.data;
  },

  async healQuestion(vetEntryId: string, selectedAnswer: string): Promise<HealAttemptResult> {
    const response = await api.post(`/vet/questions/${vetEntryId}/heal`, { selectedAnswer });
    return response.data;
  },

  async dismissQuestion(vetEntryId: string): Promise<{ dismissed: boolean; message: string }> {
    const response = await api.delete(`/vet/questions/${vetEntryId}`);
    return response.data;
  },

  async masterQuestion(vetEntryId: string, selectedAnswer: string): Promise<MasterAttemptResult> {
    const response = await api.post(`/vet/questions/${vetEntryId}/master`, { selectedAnswer });
    return response.data;
  },

  async returnQuestion(vetEntryId: string): Promise<ReturnToPracticeResult> {
    const response = await api.post(`/vet/questions/${vetEntryId}/return`);
    return response.data;
  },
};

// Rewards API
export interface StoreItem {
  id: string;
  slug: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  price: number;
  totalStock: number | null;
  remainingStock: number | null;
  isSoldOut: boolean;
}

export interface StoreCategory {
  name: string;
  items: StoreItem[];
}

export interface PointsBalance {
  currentBalance: number;
  totalEarned: number;
}

export interface PurchaseResult {
  id: string;
  itemId: string;
  itemName: string;
  itemIcon: string;
  pointsSpent: number;
  purchasedAt: string;
  newBalance: number;
}

export interface PurchaseError {
  error: string;
  currentBalance?: number;
  itemPrice?: number;
  deficit?: number;
  itemName?: string;
  remainingStock?: number;
}

export const rewardsApi = {
  async getPoints(): Promise<PointsBalance> {
    const response = await api.get('/rewards/points');
    return response.data;
  },

  async getStoreItems(): Promise<{ categories: StoreCategory[] }> {
    const response = await api.get('/rewards/store');
    return response.data;
  },

  async purchaseItem(itemId: string): Promise<PurchaseResult> {
    const response = await api.post('/rewards/purchase', { itemId });
    return response.data;
  },
};

// User API
export interface PartnerContact {
  partnerEmail: string;
  partnerPhone?: string;
  notificationPreference: 'email' | 'sms' | 'both';
  isActive: boolean;
}

export const userApi = {
  async getPartner(): Promise<PartnerContact> {
    const response = await api.get('/user/partner');
    return response.data;
  },

  async updatePartner(data: Omit<PartnerContact, 'isActive'>): Promise<PartnerContact> {
    const response = await api.put('/user/partner', data);
    return response.data;
  },
};

// Scoring API
export interface ScoringState {
  radahnRuneUnlocked: boolean;
  radahnStreakCount: number;
  rykardRuneUnlocked: boolean;
  rykardRuneEquipped: boolean;
  rykard3xCount: number;
  frenzy: {
    sessionId: string;
    difficulty: string;
    anteCost: number;
    currentMultiplier: number;
    startMultiplier: number;
    peakMultiplier: number;
    totalEarned: number;
    status: 'active' | 'cashed_out' | 'failed';
    startedAt: string;
  } | null;
}

export interface ScoringConfig {
  names: {
    bloodstain: string;
    posture: string;
    deflect: string;
    broken: string;
    frenzy: string;
    radahnRune: string;
    rykardRune: string;
    healed: string;
  };
  posture: {
    timeLimitSeconds: number;
    windows: {
      easy: { deflect: number; normal: number };
      medium: { deflect: number; normal: number };
      hard: { deflect: number; normal: number };
    };
  };
  frenzy: {
    multiplierCap: number;
    defaultFloor: number;
    rykardFloor: number;
    failurePenaltyRate: number;
  };
  ante: {
    easy: number;
    medium: number;
    hard: number;
  };
  radahn: {
    streakRequired: number;
    enhancedHardPoints: number;
  };
  rykard: {
    maxReachCountRequired: number;
  };
}

export interface FrenzyActivateResponse {
  sessionId: string;
  anteCost: number;
  startMultiplier: number;
  newBalance: number;
  difficulty: string;
}

export interface FrencyCashoutResponse {
  sessionId: string;
  totalEarned: number;
  peakMultiplier: number;
  newBalance: number;
}

export interface RuneEquipResponse {
  runeId: string;
  equipped: boolean;
  message: string;
}

export const scoringApi = {
  async getState(): Promise<ScoringState> {
    const response = await api.get('/scoring/state');
    return response.data;
  },

  async getConfig(): Promise<ScoringConfig> {
    const response = await api.get('/scoring/config');
    return response.data;
  },

  async activateFrenzy(difficulty: string): Promise<FrenzyActivateResponse> {
    const response = await api.post('/scoring/frenzy/activate', { difficulty });
    return response.data;
  },

  async cashoutFrenzy(): Promise<FrencyCashoutResponse> {
    const response = await api.post('/scoring/frenzy/cashout');
    return response.data;
  },

  async equipRune(runeId: string, equipped: boolean): Promise<RuneEquipResponse> {
    const response = await api.post('/scoring/runes/equip', { runeId, equipped });
    return response.data;
  },
};

export default api;
